// Pure helpers of the share-link screens (GEDX-FE-03): the draft of a new link and the request it
// becomes, what the space's policy allows, plain-language failures, and the status of a link.
import type { CreateShareLinkDto, ShareLinkDtoOutput, SpaceDtoOutput } from '@batiflow/api-client';
import { ApiError } from '@batiflow/client-core';
import { CreateShareLinkRequestSchema, SHARE_LINK_MAX_DAYS } from '@batiflow/contracts';
import type { TFunction } from 'i18next';

export type ShareLink = ShareLinkDtoOutput;
export type ShareLinkStatus = ShareLink['status'];

/** The document or folder a link is made for. `name` is shown in the texts. */
export type ShareTarget =
  { type: 'document'; id: string; name: string } | { type: 'folder'; id: string; name: string };

export const DEFAULT_DAYS = 7;
export const PASSWORD_MIN = 10;
export const PASSWORD_MAX = 128;
export const MAX_DOWNLOADS_LIMIT = 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

/** What the space's policy lets a link do (the policy is the ceiling, on the server too). */
export interface SpaceLimits {
  /** The space turned share links off. */
  linksOff: boolean;
  /** The space forbids downloads: a link cannot allow them. */
  downloadsOff: boolean;
  /** The space requires a watermark: every link carries one. */
  watermarkRequired: boolean;
}

export const limitsOf = (policy: SpaceDtoOutput['policy']): SpaceLimits => ({
  linksOff: !policy.shareLinksAllowed,
  downloadsOff: !policy.downloadAllowed,
  watermarkRequired: policy.watermark,
});

/** The values of the creation form. Numbers are text, as typed. */
export interface Draft {
  label: string;
  days: string;
  password: string;
  watermark: boolean;
  downloadAllowed: boolean;
  maxDownloads: string;
}

export const initialDraft = (limits: SpaceLimits): Draft => ({
  label: '',
  days: String(DEFAULT_DAYS),
  password: '',
  watermark: limits.watermarkRequired,
  downloadAllowed: !limits.downloadsOff,
  maxDownloads: '',
});

export type DraftField = 'days' | 'password' | 'maxDownloads' | 'label';
export type DraftErrors = Partial<Record<DraftField, string>>;

/** A whole number typed in a field, or null when it is empty or not one. */
export function wholeNumber(text: string): number | null {
  const trimmed = text.trim();
  return /^\d{1,6}$/.test(trimmed) ? Number(trimmed) : null;
}

/** The date a link made now with `days` days will end. Null when `days` is out of range. */
export function expiryDate(days: string, now: Date): Date | null {
  const count = wholeNumber(days);
  if (count === null || count < 1 || count > SHARE_LINK_MAX_DAYS) return null;
  return new Date(now.getTime() + count * DAY_MS);
}

export type BuiltRequest =
  { ok: true; data: CreateShareLinkDto } | { ok: false; errors: DraftErrors };

/**
 * The request made of the form, or the fields that are wrong (the keys of `errors` name the
 * field, the texts come from the screen). The contract's own schema has the last word.
 */
export function buildRequest(target: ShareTarget, draft: Draft, limits: SpaceLimits): BuiltRequest {
  const errors: DraftErrors = {};
  const days = wholeNumber(draft.days);
  if (days === null || days < 1 || days > SHARE_LINK_MAX_DAYS) errors.days = 'invalid';
  const password = draft.password;
  if (password !== '' && (password.length < PASSWORD_MIN || password.length > PASSWORD_MAX))
    errors.password = 'invalid';
  const downloads = !limits.downloadsOff && draft.downloadAllowed;
  const max = draft.maxDownloads.trim() === '' ? null : wholeNumber(draft.maxDownloads);
  if (
    downloads &&
    draft.maxDownloads.trim() !== '' &&
    (max === null || max < 1 || max > MAX_DOWNLOADS_LIMIT)
  )
    errors.maxDownloads = 'invalid';
  if (draft.label.trim().length > 120) errors.label = 'invalid';
  if (Object.keys(errors).length > 0 || days === null) return { ok: false, errors };

  const label = draft.label.trim();
  const data: CreateShareLinkDto = {
    target:
      target.type === 'document'
        ? { type: 'document', documentId: target.id }
        : { type: 'folder', folderId: target.id },
    ...(label !== '' ? { label } : {}),
    expiresInDays: days,
    ...(password !== '' ? { password } : {}),
    watermark: limits.watermarkRequired || draft.watermark,
    downloadAllowed: downloads,
    ...(downloads && max !== null ? { maxDownloads: max } : {}),
  };
  if (!CreateShareLinkRequestSchema.safeParse(data).success) {
    return { ok: false, errors: { days: 'invalid' } };
  }
  return { ok: true, data };
}

// --- Failures ------------------------------------------------------------------------------------

export type CreateFailure = 'linksOff' | 'policy' | 'forbidden' | 'notFound' | 'other';

/** What a refused creation means, for the person at the screen. */
export function createFailureOf(error: unknown): CreateFailure {
  if (!(error instanceof ApiError)) return 'other';
  if (error.code === 'SHARE_LINKS_DISABLED') return 'linksOff';
  if (error.code === 'SHARE_LINK_POLICY') return 'policy';
  if (error.status === 404) return 'notFound';
  if (error.code === 'FORBIDDEN') return 'forbidden';
  return 'other';
}

export const isPlanLimit = (error: unknown): boolean =>
  error instanceof ApiError && error.code === 'PLAN_LIMIT_REACHED';

/** Which entitlement a PLAN_LIMIT_REACHED answer is about. */
export const isDlpLimit = (error: unknown): boolean =>
  isPlanLimit(error) && error instanceof ApiError && error.problem?.entitlement === 'feature.dlp';

export type ShareText = TFunction<'shareLinks'>;

export function createFailureText(t: ShareText, tCommon: TFunction<'common'>, error: unknown) {
  const kind = createFailureOf(error);
  if (kind !== 'other') return t(`dialog.errors.${kind}`);
  if (error instanceof ApiError) {
    return tCommon(`errors.${error.code}`, { defaultValue: tCommon('errors.generic') });
  }
  return tCommon('errors.generic');
}

// --- Status --------------------------------------------------------------------------------------

/** Links that can still be opened. */
export const isLive = (link: Pick<ShareLink, 'status'>): boolean =>
  link.status === 'active' || link.status === 'exhausted';

/** Newest first. */
export const newestFirst = (a: ShareLink, b: ShareLink): number =>
  b.createdAt.localeCompare(a.createdAt);

/** The number of downloads left on a link, or null when there is no limit. */
export function downloadsLeft(link: Pick<ShareLink, 'maxDownloads' | 'downloads'>): number | null {
  return link.maxDownloads === null ? null : Math.max(0, link.maxDownloads - link.downloads);
}
